import { useT } from '../i18n/useT';
import { siteSelection as ssEn } from '../i18n/translations/en';
import { siteSelection as ssEs } from '../i18n/translations/es';
import './SiteSelection.css';

const recentDev = [
    "McDonald's", "O'Reilly Auto Parts", "Chicken Express",
    "Starbucks", "AutoZone", "Venus Gateway"
];

// City of Venus demographics: population from 2020 Census, the rest from 2024 ACS 5-year estimates
const demoStats = [
    { key: 'population', value: '4,361', census: true },
    { key: 'employment', value: '59.2%' },
    { key: 'income', value: '$106,023' },
    { key: 'housing', value: '1,753' },
    { key: 'homeownership', value: '84.4%' },
    { key: 'bachelors', value: '25.2%' },
    { key: 'uninsured', value: '16.5%' },
    { key: 'medianAge', value: '30.8' },
    { key: 'familySize', value: '3.75' },
    { key: 'under18', value: '25.1%' },
];

const popEstimates = {
    base: '4,532',
    years: [
        { year: '2020', value: '4,588' },
        { year: '2021', value: '6,121' },
        { year: '2022', value: '6,749' },
        { year: '2023', value: '7,522' },
        { year: '2024', value: '8,206' },
        { year: '2025', value: '8,870' },
    ],
};

const countyProjections = [
    { county: 'Johnson', values: ['179,927', '227,180', '254,253', '276,877', '294,713'], change: '114,786', pct: '63.8%' },
    { county: 'Ellis', values: ['192,455', '257,977', '300,439', '337,482', '386,644'], change: '176,189', pct: '91.5%' },
];

const SiteSelection = () => {
    const t = useT({ en: ssEn, es: ssEs });
    return (
        <section className="section section-alt" id="site-selection">
            <div className="container">
                <div className="section-header text-center">
                    <h2 className="section-title">{t.title}<span>{t.titleSpan}</span></h2>
                    <p className="section-subtitle">{t.subtitle}</p>
                </div>
                <div className="ss-block" id="demographics">
                    <h3 className="ss-block-title">{t.demographics.title}</h3>
                    <p>{t.demographics.body}</p>

                    <h4 className="ss-demo-subtitle">{t.demographics.statsTitle}</h4>
                    <div className="ss-demo-grid">
                        {demoStats.map((d) => (
                            <div key={d.key} className="ss-demo-card">
                                <span className="ss-demo-value">{d.value}</span>
                                <span className="ss-demo-label">
                                    {t.demographics.stats[d.key]}<sup>{d.census ? '1' : '2'}</sup>
                                </span>
                            </div>
                        ))}
                    </div>
                    <p className="ss-demo-source"><sup>1</sup> {t.demographics.sourceCensus} &nbsp;&middot;&nbsp; <sup>2</sup> {t.demographics.sourceAcs}</p>

                    <h4 className="ss-demo-subtitle">{t.demographics.estimatesTitle}</h4>
                    <div className="ss-table-wrap">
                        <table className="ss-demo-table">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>{t.demographics.estimatesBase}</th>
                                    {popEstimates.years.map((y) => <th key={y.year}>{y.year}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="ss-demo-rowlabel">{t.demographics.cityOfVenus}</td>
                                    <td>{popEstimates.base}</td>
                                    {popEstimates.years.map((y) => <td key={y.year}>{y.value}</td>)}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p className="ss-demo-source">{t.demographics.estimatesSource}</p>

                    <h4 className="ss-demo-subtitle">{t.demographics.projectionsTitle}</h4>
                    <div className="ss-table-wrap">
                        <table className="ss-demo-table">
                            <thead>
                                <tr>
                                    <th>{t.demographics.county}</th>
                                    {t.demographics.projectionYears.map((y) => <th key={y}>{y}</th>)}
                                    <th>{t.demographics.change}</th>
                                    <th>{t.demographics.pctChange}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {countyProjections.map((c) => (
                                    <tr key={c.county}>
                                        <td className="ss-demo-rowlabel">{c.county}</td>
                                        {c.values.map((v, i) => <td key={i}>{v}</td>)}
                                        <td>{c.change}</td>
                                        <td className="ss-demo-highlight">{c.pct}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className="ss-demo-source">{t.demographics.projectionsSource}</p>
                </div>
                <hr className="ss-divider" />
                <div className="ss-block" id="key-industries">
                    <h3 className="ss-block-title">{t.keyIndustries.title}</h3>
                    <p>{t.keyIndustries.body}</p>
                </div>
                <hr className="ss-divider" />
                <div className="ss-block" id="retail">
                    <h3 className="ss-block-title">{t.retail.title}</h3>
                    <div className="ss-two-col">
                        <div className="ss-image" style={{ backgroundImage: "url('/assets/images/retail-downtown.jpeg')" }}></div>
                        <p>{t.retail.body}</p>
                    </div>
                </div>
                <hr className="ss-divider" />
                <div className="ss-block" id="employers">
                    <h3 className="ss-block-title">{t.employers.title}</h3>
                    <div className="ss-employers">
                        {t.employers.items.map((e, i) => (
                            <div key={i} className="ss-employer-card">
                                <span className="ss-employer-name">{e.name}</span>
                                <span className="ss-employer-detail">{e.detail}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <hr className="ss-divider" />
                <div className="ss-block" id="transportation">
                    <h3 className="ss-block-title">{t.transportation.title}</h3>
                    <p>{t.transportation.body}</p>
                </div>
                <hr className="ss-divider" />
                <div className="ss-block" id="utilities">
                    <h3 className="ss-block-title">{t.utilities.title}</h3>
                    <p>{t.utilities.body}</p>
                </div>
                <hr className="ss-divider" />
                <div className="ss-block" id="recent-developments">
                    <h3 className="ss-block-title">{t.recentDev.title}</h3>
                    <p style={{ marginBottom: '1.5rem' }}>{t.recentDev.intro}</p>
                    <div className="ss-dev-chips">
                        {recentDev.map((d, i) => (
                            <span key={i} className="ss-chip">{d}</span>
                        ))}
                        <span className="ss-chip ss-chip-more">{t.recentDev.moreChip}</span>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default SiteSelection;
